import { LevelSim } from "@highmarch/domain";
import { icon } from "./iconsKit";
import { icbtnHtml } from "./kit";
import { resourceIconUrl } from "./portraits";
import { BossBar } from "./bossBar";
import { Biome } from "./chapters";
import { WaveChip } from "./wavePreview";
import { HudBanners } from "./hudBanners";
import { HudControls, MeasuredRect, type ScreenRect } from "./hudControls";
import { HudPills } from "./hudPills";

export { GAME_SPEEDS, controlsHidden, speedIndexOf, type ScreenRect } from "./hudControls";
export { RewardPulse } from "./hudPills";

export interface HudHandlers { onStart: () => void; onSpeed: (speed: number) => void; onSettings: () => void; onRotate: (steps: -1 | 1) => void; onPause?: () => void; onRally?: () => void; onWholeMap?: () => void; onUndo?: () => void; onEnemyChip?: (archetype: number, boss: boolean, immunity?: number) => void }

/**
 * In-level HUD (P1.10.5): top plate with level, power, points and next-cards pills, HP bar and stage segments; bottom
 * wave row. The plate's readouts are hudPills.ts, the control row hudControls.ts, the banners and callouts
 * hudBanners.ts; this class builds the DOM, places the thumb cluster and is the level's one handle on all of it.
 */
export class HudView {
  readonly root: HTMLDivElement;
  readonly controls: HTMLDivElement;
  /**
   * The thumb cluster (QoL A3): the whole-map button over the two turn buttons, at the bottom right (bottom left with
   * the `left-handed` body class), riding above whatever covers the bottom. The map also turns with a two-finger
   * twist, which no one discovers unaided. Pause stays beside speed in the control row, under the same thumb.
   */
  readonly rotate: HTMLDivElement;
  /** The DOM boss bar under the plate (Phase 2); the level subscribes it to the boss events (attachBossBar). */
  readonly bossBar: BossBar;
  private readonly pills: HudPills;
  private readonly banners: HudBanners;
  private readonly row: HudControls;
  /** Map-rotate buttons, px: smaller than the kit's 48 px icbtn so they sit under the HUD without competing with it. */
  static readonly RotateButton = 34;

  constructor(parent: HTMLElement, private readonly levelName: string, powerScore: number, h: HudHandlers) {
    this.root = document.createElement("div");
    this.root.id = "hud";
    this.root.className = "plate";
    // F10: pills carry 9 px captions (two bare numbers side by side were ambiguous), the HP bar is 130×14 with its
    // number inside, and the stage row is a segmented bar (stageSegments) instead of 10 pips for 20 stages.
    // F9: the two currency pills use painted-style CSS glyphs (a coin, a card fan) like the resource pills, instead of
    // the navigation stroke icons `coin` / `target`.
    this.root.innerHTML = `
      <div class="row top">
        ${icbtnHtml("gear", "settings", "", null, 34)}
        <div class="titles"><span class="disp gold name"></span><span class="sub"></span></div>
        <div class="spacer"></div>
        <span class="points"><div class="pill pts"><div class="ic coinic"><i class="coinfx"></i><img class="resart" decoding="sync" src="${resourceIconUrl("points")}" onerror="this.remove()" alt=""><i class="glint"></i></div><span class="tnum">0</span></div><small class="cap">POINTS</small></span>
        <span class="next" data-guide="next"><div class="pill nxt"><div class="ic blue"><i class="ring"></i><i class="cardfan"><i></i><i></i></i><img class="resart" decoding="sync" src="${resourceIconUrl("cards")}" onerror="this.remove()" alt=""></div><span class="tnum">0</span></div><small class="cap">NEXT CARD</small></span>
      </div>
      <div class="row bottom">
        <span class="heart">${icon("heart", 16, "#ff6b6b")}</span>
        <div class="hpwrap"><div class="bar hp"><div class="chip"></div><div class="fill"></div><span class="hpnum disp tnum"></span></div><i class="flash"></i></div>
        <div class="spacer"></div>
        <span class="stagebox"><small class="cap">WAVE</small><span class="disp gold stage tnum"></span></span><div class="segs"></div>
      </div>`;
    this.root.querySelector<HTMLElement>(".name")!.textContent = levelName;
    this.root.querySelector<HTMLButtonElement>(".settings")!.onclick = () => h.onSettings();
    this.pills = new HudPills(this.root, powerScore);
    this.row = new HudControls(h);
    this.controls = this.row.root;
    this.rotate = document.createElement("div");
    this.rotate.id = "rotate-map";
    // Two distinct icons rather than one mirrored with CSS: at 40 px the mirrored pair read as the same circle-arrow.
    this.rotate.className = "thumb-cluster";
    this.rotate.innerHTML = `${icbtnHtml("map", "whole", "", null, HudView.RotateButton)}<div class="turns">${icbtnHtml("turn_left", "ccw", "", null, HudView.RotateButton)}${icbtnHtml("turn_right", "cw", "", null, HudView.RotateButton)}</div>`;
    this.rotate.querySelector(".whole")!.setAttribute("aria-label", "Show the whole map");
    this.rotate.querySelector(".ccw")!.setAttribute("aria-label", "Turn the map left");
    this.rotate.querySelector(".cw")!.setAttribute("aria-label", "Turn the map right");
    this.rotate.querySelector<HTMLButtonElement>(".whole")!.onclick = () => h.onWholeMap?.();
    this.rotate.querySelector<HTMLButtonElement>(".ccw")!.onclick = () => h.onRotate(-1);
    this.rotate.querySelector<HTMLButtonElement>(".cw")!.onclick = () => h.onRotate(1);
    this.banners = new HudBanners(this.root);
    parent.append(this.root, this.controls, this.rotate, this.banners.banner, this.banners.callout);
    this.bossBar = new BossBar(parent);
  }

  // ---------------------------------------------------------------- banners and callouts (hudBanners.ts)

  announce(text: string, kind: "boss" | "warn" = "boss", ms = 2200, sub = "", portraitId = ""): void { this.banners.announce(text, kind, ms, sub, portraitId); }
  waveIncoming(stage: number, stages: number, chips: readonly WaveChip[], ms = 1700): void { this.banners.waveIncoming(stage, stages, chips, ms); }
  showCallout(title: string, sub = "", kind = ""): void { this.banners.showCallout(title, sub, kind); }
  intro(o: { chapter: number; chapterName: string; biome: Biome; level: number; copy: string; enemyIds: string[]; bossName: string | null; bossHint?: string; opensChapter: boolean; ribbon?: string; title?: string }): Promise<void> { return this.banners.intro(o); }
  floatRibbon(text: string, x: number, y: number): void { this.banners.floatRibbon(text, x, y); }
  get busyReason(): "intro" | "banner" | "callout" | null { return this.banners.busyReason; }

  // ---------------------------------------------------------------- the plate's readouts and screen flashes (hudPills.ts)

  /**
   * `points` is what the points pill shows: the sim's, less the kill rewards whose rounds are still in the air
   * (level/impactWaits.ts PendingRewards), so the pill rolls up as the round lands.
   */
  refresh(sim: LevelSim, points = sim.points): void {
    this.pills.refresh(sim, points);
    this.row.refresh(sim);
  }
  hitFlash(): void { this.pills.hitFlash(); }
  setLastStand(depth: number | null): void { this.pills.setLastStand(depth); }
  heartbeatPulse(): void { this.pills.heartbeatPulse(); }
  flyCoins(screenX: number, screenY: number, n: number): void { this.pills.flyCoins(screenX, screenY, n); }

  // ---------------------------------------------------------------- the control row (hudControls.ts)

  get speed(): number { return this.row.speed; }
  setSpeedOverride(n: number | null): void { this.row.setSpeedOverride(n); }
  setPaused(on: boolean): void { this.row.setPaused(on); }
  setRallyArmed(on: boolean): void { this.row.setRallyArmed(on); }
  rallyNote(text: string, ms: number): void { this.row.rallyNote(text, ms); }
  setSheetLift(px: number | null): void { this.row.setSheetLift(px); }
  setUndoable(on: boolean): void { this.row.setUndoable(on); }
  setControlsSuppressed(on: boolean): void { this.row.setControlsSuppressed(on); }

  // ---------------------------------------------------------------- the thumb cluster

  /**
   * The thumb cluster rides `px` above the bottom edge: over the card tray while one is up, else over the control row
   * and its wave strip (CSS default). null drops it back to the default. Written only on a change.
   */
  setClusterLift(px: number | null): void {
    const lift = px === null ? "" : `${Math.round(px + HudView.ClusterGap)}px`;
    if (this.rotate.style.bottom !== lift) this.rotate.style.bottom = lift;
    this.row.setTrayLift(px);
  }
  /** A feature's chip on top of the thumb cluster (the pet's portrait: ui/pets/petHudChip.ts); it rides with the cluster. */
  addClusterChip(el: HTMLElement): void { this.rotate.prepend(el); }
  /** Gap (px) between the thumb cluster and the tray under it. */
  static readonly ClusterGap = 10;

  /**
   * The screen rectangles of the HUD's controls that sit over the map, for the edge markers to keep clear of: the thumb
   * cluster, and pause + speed while they ride on the card tray or the tower sheet. Each is measured only when its
   * lift, shape, side (left-handed) or the viewport changed: the markers ask every frame, and a layout read there
   * would force a synchronous layout whenever the HUD text had just changed.
   */
  mapControlRects(): readonly ScreenRect[] {
    const side = `${document.body.classList.contains("left-handed")}|${innerWidth}x${innerHeight}`;
    const out: ScreenRect[] = [];
    const cluster = this.rotate.classList.contains("hidden") ? null
      : this.clusterRect.get(`${this.rotate.style.bottom}|${side}`, () => [this.rotate.getBoundingClientRect()]);
    if (cluster) out.push(cluster);
    const lifted = this.row.liftedRect(side);
    if (lifted) out.push(lifted);
    return out;
  }
  private readonly clusterRect = new MeasuredRect();
  /** The whole-map button lit while the whole map is shown (a second press zooms back in). */
  setWholeMap(on: boolean): void { this.wholeMapOn = on; this.rotate.querySelector(".whole")?.classList.toggle("on", on); }
  get wholeMapShown(): boolean { return this.wholeMapOn; }
  private wholeMapOn = false;

  dispose(): void { this.banners.dispose(); this.row.dispose(); this.pills.dispose(); this.bossBar.dispose(); this.root.remove(); this.rotate.remove(); }
}
